import { useDeferredValue, useEffect, useId, useMemo, useState } from 'react';
import { flexRender, Table } from '@tanstack/react-table';
import {
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  getCommonColumnPinningStyles,
  getCommonColumnPinningStylesForHeader,
} from '../../../utils';

interface ReusableTableProps<T> {
  table: Table<T>;
  showFilters?: boolean;
  activeSort?: { id: string; desc: boolean } | null;
}

const NON_FILTERABLE_COLUMN_IDS = new Set(['action', 'actions', 'select']);

const normalizeSearchText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim();

const valueToSearchText = (
  value: unknown,
  depth = 0,
  visited = new WeakSet<object>(),
): string => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'yes true' : 'no false';
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value
      .map(item => valueToSearchText(item, depth + 1, visited))
      .join(' ');
  }
  if (typeof value === 'object') {
    if (visited.has(value) || depth > 2) return '';
    visited.add(value);
    const dateLikeValue = value as { format?: (pattern: string) => string };
    if (typeof dateLikeValue.format === 'function') {
      return dateLikeValue.format('YYYY-MM-DD');
    }
    return Object.values(value as Record<string, unknown>)
      .map(item => valueToSearchText(item, depth + 1, visited))
      .join(' ');
  }
  return String(value);
};

const humanizeColumnId = (columnId: string): string =>
  columnId
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, character => character.toUpperCase());

function LineItemTable<T>({
  table,
  showFilters: externalShowFilters,
  activeSort,
}: ReusableTableProps<T>) {
  const headers = table.getHeaderGroups()?.[0]?.headers ?? [];
  const baseRows = table.getRowModel().rows;
  const filterRegionId = useId();
  const [internalShowFilters, setInternalShowFilters] = useState(false);
  const showFilters = externalShowFilters !== undefined ? externalShowFilters : internalShowFilters;
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );
  const deferredColumnFilters = useDeferredValue(columnFilters);
  const isFiltering = columnFilters !== deferredColumnFilters;

  const filterableHeaders = useMemo(
    () =>
      headers.filter(header => {
        const id = header.column.id.toLowerCase();
        return (
          header.column.columnDef.enableColumnFilter !== false &&
          !NON_FILTERABLE_COLUMN_IDS.has(id)
        );
      }),
    [headers],
  );

  const filterableColumnIds = useMemo(
    () => filterableHeaders.map(header => header.column.id),
    [filterableHeaders],
  );

  useEffect(() => {
    const visibleColumnIds = new Set(filterableColumnIds);
    setColumnFilters(current => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([columnId]) =>
          visibleColumnIds.has(columnId),
        ),
      );
      return Object.keys(next).length === Object.keys(current).length
        ? current
        : next;
    });
  }, [filterableColumnIds]);
  const activeFilters = useMemo(
    () =>
      Object.entries(deferredColumnFilters)
        .map(
          ([columnId, value]) =>
            [columnId, normalizeSearchText(value)] as const,
        )
        .filter(([, value]) => Boolean(value)),
    [deferredColumnFilters],
  );
  const activeFilterCount = Object.values(columnFilters).filter(value =>
    value.trim(),
  ).length;

  // Build the searchable representation once per data change. Filtering then
  // stays linear and avoids repeatedly walking select/date objects per keystroke.
  const searchIndex = useMemo(
    () =>
      new Map(
        baseRows.map(row => [
          row.id,
          new Map(
            filterableColumnIds.map(columnId => [
              columnId,
              normalizeSearchText(valueToSearchText(row.getValue(columnId))),
            ]),
          ),
        ]),
      ),
    [baseRows, filterableColumnIds],
  );

  const rows = useMemo(() => {
    let filtered = baseRows;
    if (activeFilters.length > 0) {
      filtered = baseRows.filter(row =>
        activeFilters.every(([columnId, filterValue]) =>
          searchIndex.get(row.id)?.get(columnId)?.includes(filterValue),
        ),
      );
    }
    if (!activeSort) return filtered;
    const { id, desc } = activeSort;
    return [...filtered].sort((a, b) => {
      const valA = a.getValue(id);
      const valB = b.getValue(id);
      if (valA == null && valB == null) return 0;
      if (valA == null) return desc ? 1 : -1;
      if (valB == null) return desc ? -1 : 1;
      if (typeof valA === 'number' && typeof valB === 'number') {
        return desc ? valB - valA : valA - valB;
      }
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      return desc ? strB.localeCompare(strA) : strA.localeCompare(strB);
    });
  }, [activeFilters, baseRows, searchIndex, activeSort]);

  const removeFilter = (columnId: string) =>
    setColumnFilters(previous => {
      const next = { ...previous };
      delete next[columnId];
      return next;
    });

  return (
    <div
      className="line-item-table overflow-hidden bg-white print:max-h-none"
      role="region"
      aria-label="Line Items"
    >
      {activeFilterCount > 0 && (
        <div className="line-item-filter-summary flex flex-wrap items-center gap-1.5 border-b border-slate-200/70 bg-indigo-50/20 px-3.5 py-1.5 print:hidden">
          <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Filtered By:
          </span>
          {Object.entries(columnFilters)
            .filter(([, value]) => value.trim())
            .map(([columnId, value]) => (
              <button
                key={columnId}
                type="button"
                title={`Remove ${humanizeColumnId(columnId)} filter`}
                className="inline-flex max-w-64 items-center gap-1 rounded-md border border-indigo-200/80 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 shadow-2xs transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                onClick={() => removeFilter(columnId)}
              >
                <span className="font-semibold text-[#5B5CE2]">
                  {humanizeColumnId(columnId)}:
                </span>
                <span className="truncate">{value}</span>
                <XMarkIcon className="size-3 shrink-0 text-slate-400" />
              </button>
            ))}
        </div>
      )}
      <div className="max-h-[62vh] overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <caption className="sr-only">Travel Request Line Items</caption>
          <thead className="line-item-table-head sticky top-0 z-[11] border-b border-slate-200/80 bg-slate-50/95 backdrop-blur-xs">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="whitespace-nowrap px-3.5 py-2.5 text-left text-xs font-bold tracking-tight text-slate-700 border-b border-slate-200/70"
                    style={{
                      ...getCommonColumnPinningStylesForHeader(
                        header.column,
                        table,
                      ),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </th>
                ))}
              </tr>
            ))}
            {showFilters && (
              <tr
                id={filterRegionId}
                className="border-t border-[#DDD9FF] bg-[#FBFAFF] print:hidden"
              >
                {headers.map(header => {
                  const canFilter = filterableHeaders.some(
                    filterHeader => filterHeader.id === header.id,
                  );
                  return (
                    <th
                      key={`filter-${header.id}`}
                      className="px-1.5 py-1"
                      style={getCommonColumnPinningStylesForHeader(
                        header.column,
                        table,
                      )}
                    >
                      {canFilter ? (
                        <label className="relative block">
                          <span className="sr-only">
                            Search {humanizeColumnId(header.column.id)}
                          </span>
                          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-slate-400" />
                          <input
                            type="search"
                            value={columnFilters[header.column.id] || ''}
                            placeholder={`Search ${humanizeColumnId(header.column.id)}`}
                            className="h-6.5 w-full min-w-24 rounded-md border border-slate-200 bg-white py-0.5 pl-6 pr-1.5 text-[11px] font-normal text-slate-700 placeholder:text-slate-400 outline-none transition hover:border-slate-300 focus:border-[#5B5CE2] focus:ring-1 focus:ring-indigo-100"
                            onClick={event => event.stopPropagation()}
                            onKeyDown={event => {
                              if (event.key === 'Escape')
                                removeFilter(header.column.id);
                            }}
                            onChange={event =>
                              setColumnFilters(previous => ({
                                ...previous,
                                [header.column.id]: event.target.value,
                              }))
                            }
                          />
                        </label>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>

          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  className="px-3 py-8 text-center text-xs font-medium"
                >
                  <div className="flex flex-col items-center gap-2 text-xs font-semibold text-slate-500">
                    <div className="inline-grid size-9 place-items-center rounded-full bg-slate-100 text-slate-400">
                      <MagnifyingGlassIcon className="size-5" />
                    </div>
                    <span>
                      {activeFilterCount
                        ? 'No Items Match The Selected Filters'
                        : 'No Line Items Recorded Yet'}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr
                  key={row.id}
                  className="group border-t border-slate-100/80 bg-white transition-colors duration-150 hover:bg-indigo-50/20"
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className="whitespace-nowrap px-3.5 py-2.5 text-left text-xs text-slate-700 group-hover:text-slate-900"
                      style={{
                        ...getCommonColumnPinningStyles(cell.column, table),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LineItemTable;
